export type DiffKind = 'context' | 'added' | 'removed';

export interface DiffEntry {
  kind: DiffKind;
  beforeLine: number | null;
  afterLine: number | null;
  text: string;
}

export function computeLineDiff(before: string, after: string): DiffEntry[] {
  const beforeLines = normalizeLines(before);
  const afterLines = normalizeLines(after);
  const beforeCount = beforeLines.length;
  const afterCount = afterLines.length;
  const lcs = createMatrix(beforeCount + 1, afterCount + 1);

  for (let i = beforeCount - 1; i >= 0; i -= 1) {
    for (let j = afterCount - 1; j >= 0; j -= 1) {
      if (beforeLines[i] === afterLines[j]) {
        lcs[i][j] = lcs[i + 1][j + 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }
  }

  const entries: DiffEntry[] = [];
  let i = 0;
  let j = 0;
  let beforeIndex = 1;
  let afterIndex = 1;

  while (i < beforeCount && j < afterCount) {
    const beforeLine = beforeLines[i];
    const afterLine = afterLines[j];
    if (beforeLine === afterLine) {
      entries.push({ kind: 'context', beforeLine: beforeIndex, afterLine: afterIndex, text: beforeLine });
      i += 1;
      j += 1;
      beforeIndex += 1;
      afterIndex += 1;
      continue;
    }

    const skipBefore = lcs[i + 1][j];
    const skipAfter = lcs[i][j + 1];
    if (skipBefore >= skipAfter) {
      entries.push({ kind: 'removed', beforeLine: beforeIndex, afterLine: null, text: beforeLine });
      i += 1;
      beforeIndex += 1;
    } else {
      entries.push({ kind: 'added', beforeLine: null, afterLine: afterIndex, text: afterLine });
      j += 1;
      afterIndex += 1;
    }
  }

  while (i < beforeCount) {
    entries.push({ kind: 'removed', beforeLine: beforeIndex, afterLine: null, text: beforeLines[i] });
    i += 1;
    beforeIndex += 1;
  }

  while (j < afterCount) {
    entries.push({ kind: 'added', beforeLine: null, afterLine: afterIndex, text: afterLines[j] });
    j += 1;
    afterIndex += 1;
  }

  return entries;
}

function normalizeLines(value: string): string[] {
  if (!value) {
    return [];
  }
  const normalized = value.replace(/\r\n/g, '\n');
  return normalized.split('\n');
}

function createMatrix(rows: number, cols: number): number[][] {
  const matrix: number[][] = new Array(rows);
  for (let i = 0; i < rows; i += 1) {
    matrix[i] = new Array(cols).fill(0);
  }
  return matrix;
}
